import JamSpinner from '@/src/components/common/JamSpinner/JamSpinner';
import AllJamRoutes from '@/src/components/root/AllJamRoutes';
import { SrStatusText } from '@/src/components/root/SRStatusText/SRStatusText';
import AIAssistantContainer from '@/src/components/common/AIAssistant/AIAssistantContainer';
import { config } from '@/src/config/app-config';
import { useLoading } from '@/src/hooks/store/useLoading';
import { useSideNav } from '@/src/hooks/store/useSideNav';
import { useIsMobile } from '@/src/hooks/useIsMobile';
import { useModalManager } from '@/src/hooks/useModalManager';
import { useSideNavBreadCrumbVisibility } from '@/src/hooks/useSideNavBreadCrumbVisibility';
import { RoutePath } from '@/src/RoutePath';
import { useAuth } from '@/src/store/auth.context';
import { isNonGameRoute } from '@/src/utils/route-visibility.utils';
import { useJamLeaderboard } from '@/src/store/jam-leaderboard.context';
import { applyRumCookieConsent, configureAwsRum } from '@/src/utils/awsrum.utils';
import localStorageTTL from '@/src/utils/localStorageTTL.utils';
import { gradientWithoutSidebar, jamChallengesRegex } from '@/src/utils/myjam.utils';
import { AppLayout } from '@amzn/awsui-components-react';
import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useActiveRouteDetails } from '../../hooks/useActiveRouteDetails';
import { useCookieConsent } from '../../store/cookie-consent.context';
import { useSplitPanel } from '../../store/split-panel.context';
import { useToolPanel } from '../../store/tool-panel.context';
import { useUser } from '../../store/user.context';
import { i18nKeys } from '../../utils/i18n.utils';
import AbsLoader from '../common/AbsLoader';
import { LoadingBar } from '../common/LoadingBar';
import SplitPanelInterface from '../common/SplitPanelInterface';
import { ToolSets } from '../tools/ToolSets';
import styles from './App.module.scss';
import BreadCrumbs from './BreadCrumbs';
import { Footer } from './Footer/Footer';
import GlobalSideNav from './GlobalSideNav/GlobalSideNav';
import { NewHeader } from './Header';
import { I18nProvider } from '@amzn/awsui-components-react/polaris/i18n';
import messages from '@amzn/awsui-components-react/polaris/i18n/messages/all.all';
import { getLanguageCodeSafe } from '@/src/utils/locale.utils';
import TopLevelFlashbars from '@/src/components/root/TopLevelFlashbars/TopLevelFlashbars';

configureAwsRum();
localStorageTTL.init();

const TopHeader = () => {
  return (
    <Suspense fallback={<LoadingBar />}>
      <NewHeader />
    </Suspense>
  );
};

const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isHome, activeRoute, isAiTeammateGameView } = useActiveRouteDetails();
  const location = useLocation();
  const { user, loading } = useUser();
  const { authClient } = useAuth();
  const { shortbread, initializeCookies } = useCookieConsent();
  const { showSplitPanel, toggleSplitPanel, splitPanelOpen } = useSplitPanel();
  const { isPresenterMode } = useJamLeaderboard();
  const { showToolPanel, toggleToolPanel, toolPanelOpen, toolPanelWidth } = useToolPanel();
  const { removeAllModals } = useModalManager();
  const { isSideNavVisible, isBreadCrumbVisible } = useSideNavBreadCrumbVisibility();
  const { loading: isLoading } = useLoading();
  const { showSideNav, setShowSideNav } = useSideNav();
  const isMobile = useIsMobile();
  const openingToolRef = useRef<HTMLElement>();

  const safeLangLocale = useMemo(() => {
    return getLanguageCodeSafe(i18n?.language);
  }, [i18n.language]);

  useEffect(() => {
    removeAllModals();
  }, [location.pathname, location.search]);

  const ignoreResizeErrHandler = useRef((err: ErrorEvent) => {
    if (
      !err.message.includes('ResizeObserver loop limit exceeded') &&
      !err.message.includes('ResizeObserver loop completed with undelivered notifications')
    ) {
      throw err;
    }
  });

  useEffect(() => {
    window.addEventListener('error', ignoreResizeErrHandler.current);
    return () => window.removeEventListener('error', ignoreResizeErrHandler.current);
  }, []);

  useEffect(() => {
    // check for the IDP query parameter on the app's every route change / state update
    void authClient.handleIdpQueryParam(user);
    if (shortbread === undefined) {
      initializeCookies();
    }
    // in EU geo locations, will show the cookie consent banner.
    // (will show it once per 365 days at most per domain)
    shortbread?.checkForCookieConsent();
    // Keep RUM cookie usage in sync with shortbread's performance consent
    // (getConsentCookie() is undefined until the user resolves consent).
    applyRumCookieConsent(shortbread?.getConsentCookie()?.performance === true);
  }, [shortbread, authClient, initializeCookies, user, showSplitPanel]);

  useLayoutEffect(() => {
    if (showToolPanel && document.activeElement) {
      openingToolRef.current = document.activeElement as HTMLElement;
    }

    if (!showToolPanel && openingToolRef.current) {
      openingToolRef.current.focus();
    }
  }, [showToolPanel]);

  const applyGradientBg = useMemo(
    () =>
      gradientWithoutSidebar.test(location.pathname)
        ? gradientWithoutSidebar.test(location.pathname)
        : jamChallengesRegex.test(location.pathname),
    [location.pathname]
  );

  const showAIAssistant = useMemo(() => {
    return config.ff.enableAIAssistant && !!user && user.isAmazonian && isNonGameRoute(activeRoute);
  }, [user, activeRoute]);

  const getContent = () => {
    if (loading) {
      return <JamSpinner />;
    }
    return <AllJamRoutes />;
  };

  const getClassNameBasedOnLocation = useCallback(() => {
    const pathName = location.pathname.split('/').join('-');
    return pathName.slice(1);
  }, [location]);

  const isReportPage = location.pathname.endsWith('/reports');

  return (
    <Suspense fallback={<LoadingBar />}>
      <I18nProvider messages={[messages]} locale={safeLangLocale}>
        <div className={styles.container}>
          <SrStatusText />
          {!isPresenterMode && <TopHeader />}
          <div className={getClassNameBasedOnLocation()} data-testid="app-layout-container">
            <AppLayout
              disableContentPaddings={isHome || activeRoute === RoutePath.NEW_AI_EVENT || isAiTeammateGameView}
              navigationOpen={showSideNav}
              navigationHide={!isMobile && !isSideNavVisible}
              onNavigationChange={({ detail }) => setShowSideNav(detail.open)}
              navigation={<GlobalSideNav />}
              notifications={<TopLevelFlashbars />}
              stickyNotifications
              breadcrumbs={!isBreadCrumbVisible ? null : <BreadCrumbs />}
              headerSelector={'#header'}
              footerSelector={'#footer'}
              toolsOpen={showToolPanel && toolPanelOpen}
              toolsWidth={toolPanelWidth}
              content={getContent()}
              tools={<ToolSets />}
              toolsHide={!showToolPanel}
              onToolsChange={() => toggleToolPanel(!toolPanelOpen)}
              splitPanelOpen={splitPanelOpen}
              onSplitPanelToggle={() => toggleSplitPanel()}
              splitPanel={<SplitPanelInterface />}
              className={applyGradientBg ? 'gradient-bg' : ''}
              ariaLabels={{
                navigationClose: t(i18nKeys.general.navigationClose),
                tools: t(i18nKeys.general.toolsHelp),
                toolsClose: t(i18nKeys.general.toolsClose),
                toolsToggle: t(i18nKeys.general.toolsToggle),
                navigation: t(i18nKeys.sideNavigation.navigation),
                navigationToggle: t(i18nKeys.sideNavigation.navigationToggle),
                notifications: t(i18nKeys.general.notifications),
              }}
              maxContentWidth={isPresenterMode || isReportPage ? Number.MAX_VALUE : undefined}
            />
          </div>
          <Footer />
          {isLoading && <AbsLoader />}
          {showAIAssistant && <AIAssistantContainer />}
        </div>
      </I18nProvider>
    </Suspense>
  );
};

export default App;
